import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { IAgoraRTCRemoteUser, ICameraVideoTrack } from 'agora-rtc-react';

import { useAppContext } from '@/app/AppContext';
import VideoDisplay, { VideoTrackInfo } from '@/app/components/VideoDisplay';
import { useMeetingData, useUserData } from '@/app/queries/MeetingQuery/context';

import { cn } from '@/lib/utils';
import { EVENTS, gtag } from '@/lib/gtag';

interface VideoTracksDisplayProps {
  localCameraTrack: ICameraVideoTrack;
  remoteUsers: IAgoraRTCRemoteUser[];
  showVideoTracks: boolean;
}

export default function VideoTracksDisplay({ localCameraTrack, remoteUsers, showVideoTracks }: VideoTracksDisplayProps) {
  const { camera, microphone } = useAppContext();

  const userData = useUserData();
  const { participants } = useMeetingData();

  const knownUids = useMemo(() => new Set(
    participants.flatMap(p => [p.user_id, p.screen_share_id])
  ), [participants]);

  const filteredRemoteUsers = useMemo(
    () => remoteUsers.filter(user => knownUids.has(user.uid as number)),
    [remoteUsers, knownUids]
  );

  const [videoTracks, setVideoTracks] = useState<VideoTrackInfo[]>([]);
  const hasChosenLayout = useRef(false);

  const handleVideoClick = useCallback((clickedVideoTrack: VideoTrackInfo) => {
    hasChosenLayout.current = true;
    gtag(EVENTS.FOCUS_STREAM);

    setVideoTracks(videoTracks => videoTracks.map(video => ({
      ...video,
      isEnlarged: video.source === clickedVideoTrack.source
    })));
  }, []);

  useEffect(function initTracks() {
    setVideoTracks(prevTracks => {
      const tracks: VideoTrackInfo[] = [
        {
          uid: userData.uid,
          source: localCameraTrack,
          sourceType: 'local',
          isEnlarged: prevTracks.find(track => track.uid === userData.uid)?.isEnlarged ?? true,
          hasAudio: microphone.isEnabled,
          hasVideo: camera.isEnabled,
        },
        ...filteredRemoteUsers.map(user => {
          const existingTrack = prevTracks.find(track => track.uid === user.uid);

          return {
            uid: user.uid as number,
            source: user,
            sourceType: 'remote' as VideoTrackInfo['sourceType'],
            isEnlarged: existingTrack ? existingTrack.isEnlarged : false,
            hasAudio: user.hasAudio,
            hasVideo: user.hasVideo,
          }
        }),
      ];
      // The host is on the call to SEE the customer: the moment the other side
      // first appears, they take the stage — unless the host has already
      // picked a layout by tapping. The customer keeps their viewfinder.
      const remoteJustArrived =
        tracks.some(track => track.sourceType === 'remote')
        && !prevTracks.some(track => track.sourceType === 'remote');
      if (userData.isHost && !hasChosenLayout.current && remoteJustArrived) {
        const stageUid = tracks.find(track => track.sourceType === 'remote')!.uid;
        return tracks.map(track => ({ ...track, isEnlarged: track.uid === stageUid }));
      }
      return tracks;
    });
  }, [localCameraTrack, filteredRemoteUsers, userData.uid, userData.isHost, microphone.isEnabled, camera.isEnabled]);

  const stageTracks = videoTracks.filter(video => video.isEnlarged);
  const pictureInPictureTracks = videoTracks.filter(video => !video.isEnlarged);

  // On a phone the video is the whole screen and everything else floats on top
  // of it. A desktop has room to lay the call out properly instead: the stage
  // becomes a panel and the other participants stand beside it in a column.
  return (
    <div
      className={cn(
        'absolute inset-0 md:relative md:inset-auto md:flex md:min-h-0 md:flex-1 md:gap-4 md:px-4',
        { 'invisible': !showVideoTracks },
      )}>
      <div className="absolute inset-0 md:relative md:inset-auto md:min-w-0 md:flex-1 md:overflow-hidden md:rounded-2xl md:bg-black">
        {stageTracks.map(video => (
          <Fragment key={video.uid}>
            <VideoDisplay
              className={cn('MainVideo absolute inset-0', { 'is-disabled': !video.source })}
              onClick={handleVideoClick} variant="stage" videoTrack={video} />
          </Fragment>
        ))}
      </div>

      {pictureInPictureTracks.length > 0 && (
        <div className="absolute right-4 top-safe-top z-20 flex flex-col gap-3 md:static md:w-64 md:shrink-0">
          {pictureInPictureTracks.map(video => (
            <div
              className="relative aspect-portrait w-24 cursor-pointer overflow-hidden rounded-xl shadow-lg ring-1 ring-white/20 md:aspect-video md:w-full"
              key={video.uid}
              title="Swap with the main view">
              <VideoDisplay onClick={handleVideoClick} videoTrack={video} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
